import React, {createContext, useCallback, useEffect} from "react";
import {
    addReportJob, completeReportStatus,
    removeReportJob,
    setReportQueue,
    updateReportStatus
} from "../state/slices/ReportJobQueue/ReportJobQueue";
import apiClient from "../auth/apiClient";
import  ReportQueueService from "../services/ReportQueueService";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useCoreEntityContext } from "../hooks/useCoreEntitySlice";
import useHubContext from "../hooks/useHubContext";
import {REPORT_QUEUE_PROCESSING, REPORT_QUEUE_COMPLETED, ENTITY_TYPE, REPORT_QUEUE_STATUS_CODES} from "../constants";
import {selectParentEntity} from "../state/slices/CoreEntity";
import {FindEntity} from "../state/slices/entities";
import moment from "moment/moment";

export const ReportQueueContext = createContext({});

const reportQueueService = new ReportQueueService(apiClient);
const STATUS_MAP = {
    REPORT_QUEUE_PROCESSING: REPORT_QUEUE_STATUS_CODES.PROCESSING,
    REPORT_QUEUE_ABANDONED: REPORT_QUEUE_STATUS_CODES.ABANDONED,
    REPORT_QUEUE_COMPLETED: REPORT_QUEUE_STATUS_CODES.COMPLETED,
};

export const ReportQueueProvider = ({ children }) => {
    const useCoreEntitySlice = useCoreEntityContext();
    const entityID = useSelector((state) => useCoreEntitySlice ? state.coreEntities.ContextID : state.entities?.ContextID);
    const dispatch = useDispatch();
    const {Connected: PortalHubConnected, portalHub} = useHubContext();
    const parentEntity = useSelector((state) => {
        if (useCoreEntitySlice) return selectParentEntity(entityID)(state);

        const entity = FindEntity(state.entities?.EntityList ?? [], entityID);
        return FindEntity(state.entities?.EntityList ?? [], entity.parententityid);
    }, shallowEqual);


    const handleHubMessage = useCallback((rawMessage, fallbackStatus) => {
        try {
            const outer = JSON.parse(rawMessage);
            const topic = outer.Topic || fallbackStatus;
            const inner = JSON.parse(outer.Message);
            const rqid = inner.RqId || inner.queue?.RqId;
            const rqStatusId = inner.RqStatusId || inner.queue?.RqStatusId;

            switch (topic) {
                case "REPORT_QUEUE_ADDED": {
                    const normalized = normalizeQueueItem(inner);
                    if (!normalized) return;
                    dispatch(addReportJob(normalized));
                    break;
                }
                case "REPORT_QUEUE_CANCELED":
                    dispatch(removeReportJob({rqId: rqid}));
                    break;
                case "REPORT_QUEUE_PROCESSING":
                    dispatch(updateReportStatus({rqId: rqid, rqStatusId: STATUS_MAP[topic]}));
                    break;
                case "REPORT_QUEUE_ABANDONED":
                    break;
                case "REPORT_QUEUE_COMPLETED":
                    dispatch(completeReportStatus({
                        rqId: rqid,
                        rqStatusId: rqStatusId,
                        rqCompletionTime: moment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
                    }));
                    break;

                default:
                    console.warn("Unhandled ReportQueue topic:", topic);
            }
        } catch (err) {
            console.error("Failed to handle report queue message:", err, rawMessage);
        }
    }, []);

    const normalizeQueueItem = (msg) => {
        if (!msg) return null;

        if (msg.rqId) return msg;

        const q = msg.queue;
        return {
            facilityId: q.FacilityId,
            rqStatusId: q.RqStatusId,
            rqProcessorId: q.RqProcessorId,
            userId: q.UserId,
            userName: msg.UserName,
            reportParameters: (() => {
                try {
                    return JSON.parse(q.ReportParameters);
                } catch {
                    return q.ReportParameters;
                }
            })(),
            rqStoragePath: q.RqStoragePath,
            reportId: q.ReportId,
            scheduledReportId: q.ScheduledReportId,
            rqCompletionTime: q.RqCompletionTime,
            rqId: q.RqId,
            rqCreatedTime: q.RqCreatedTime,
            rqPickupTime: q.RqPickupTime,
            version: q.Version,
        };
    };

    useEffect(() => {
        const fetchReportQueue = async () => {
            try {
                const entityId = parentEntity.typeid === ENTITY_TYPE.FacilityGroup ? parentEntity.entityid : entityID;

                if(!entityId) return;
                const response = await reportQueueService.getReportQueueRecords(entityId);
                if(response?.data) {
                    dispatch(setReportQueue(response.data));
                }
            } catch (err) {
                console.error(err);
            }
        };

        fetchReportQueue();
    }, [entityID]);

    useEffect(() => {
        if (!entityID || !PortalHubConnected) return;

        const topics = Object.keys(STATUS_MAP).concat([
            "REPORT_QUEUE_ADDED",
            "REPORT_QUEUE_CANCELED",
        ]);

        topics.forEach((t) => {
            portalHub.subscribe(t, (msg) =>
                handleHubMessage(msg, t)
            );
        });

        return () => {
            topics.forEach((t) => portalHub.unsubscribe(t));
        };
    }, [entityID, PortalHubConnected, portalHub, handleHubMessage]);

    const alterGroupStatus = async (entityId, add) => {
        try {
            await portalHub.invoke(add ? "AddToReportQueueGroup" : "RemoveFromReportQueueGroup", {
            entityID: entityId,
            });
        } catch (error) {
            console.error("Couldn't update Report Queue Group in Hub", entityID, error);
        }
    }
      
    useEffect(() => {
        const entityId = parentEntity.typeid === ENTITY_TYPE.FacilityGroup ? parentEntity.entityid : entityID;
        if (PortalHubConnected) {
          alterGroupStatus(entityId, true);
        }
    
        return () => {
          alterGroupStatus(entityId, false);
        };
    }, [PortalHubConnected, entityID]);

    return (
        <ReportQueueContext.Provider value={{ }}>
            {children}
        </ReportQueueContext.Provider>
    );
};